import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useGetCustomDatasources } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCounter } from '@/utils/collectionUtils';
import { addCustomDatasourceUrl } from '@/utils/urls';

import { useGetCollectionTableProps } from './config';
import { CustomDatasourceRegisterActions } from './CustomDatasourceRegisterActions';
import { CustomDatasourceRegisterToolbar } from './CustomDatasourceRegisterToolbar';
import { CUSTOM_DATASOURCE_SMART_COPY } from './customDatasourceSmartCopy';
import type { CustomDatasourceTableFields } from './types';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'customDatasources',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const navigate = useNavigate();

  const { data, loading } = useGetCustomDatasources({ queryArgs: {} });

  const tableProps = useGetCollectionTableProps(data?.custom_datasource);

  const title = st('register_title');
  const counter = getCounter(tableProps.totalItemsCount, loading);
  const openCreate = useCallback(() => {
    navigate(addCustomDatasourceUrl());
  }, [navigate]);

  // Reads OpenFeature (flagd locally/eph, Datadog on dev-cloud/prod), or the
  // seeded Meta.features override for e2e test orgs.
  const smartCopyFlagEnabled = useIsFeatureFlagEnabled(
    CUSTOM_DATASOURCE_SMART_COPY.flagKey
  );

  // The dropdown lives in the page-header actions slot, which the object toolbar
  // replaces via headerOverride — so gate it (and the tickboxes) off under the
  // toolbar, else rows are selectable with no reachable Copy.
  const isSmartCopyEnabled = smartCopyFlagEnabled && !objectToolbarEnabled;

  const { hasPermission: canInsert, loading: canInsertLoading } =
    useHasPermissionQuery('insert:custom_datasource');

  // Single-select drives the "Copy" action. The register has no Delete control
  // (Delete lives on the edit form), so selection is purely insert-gated, and
  // row selection is net-new to this register.
  const [selected, setSelected] = useState<CustomDatasourceTableFields[]>([]);
  const showSelection = isSmartCopyEnabled && canInsert && !canInsertLoading;

  return (
    <PageLayout
      helpTranslationKey={'customDatasources.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-172) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <CustomDatasourceRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            onCreate={openCreate}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton tableProps={tableProps} entityLabel={title} />
          <Permission permission={'insert:custom_datasource'}>
            <CustomDatasourceRegisterActions
              isSmartCopyEnabled={isSmartCopyEnabled}
              selectedCount={selected.length}
            />
          </Permission>
        </SpaceBetween>
      }
      meta={{
        title: st('register_title'),
      }}
    >
      <Table
        {...tableProps}
        loading={loading}
        selectionType={showSelection ? 'single' : undefined}
        selectedItems={selected}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems)}
      />
    </PageLayout>
  );
};

export default Page;
